import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";

import useCategoriesPartial from "../../data/useCategoriesPartial";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import { IWarehouseLocation } from "../../models/Location";
import { FirstListedBucket } from "../../models/listingOptimisation/IListingInsights";
import classNames from "../../utils/classNames";
import { FIRST_LISTED_BUCKET_LABELS } from "../../utils/listingAge";
import { MISSING_FIELD_OPTIONS } from "../ebayListings/listedTable/missingFields";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import TableFilterPanel, { ITableFilterPill } from "../shared/Tables/TableFilterPanel";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import {
  DEFAULT_HIDE_REVIEWED_DAYS,
  IQueueFilters,
  LocationSearchMode,
  QUEUE_FILTERS_STORAGE_KEY,
  QUEUE_SORT_OPTIONS,
  QueueSortKey,
  defaultQueueFilters,
  hasVehicleGroupFilter,
} from "./queueFilterState";

interface IQueueFiltersProps {
  filters: IQueueFilters;
  onChange: (next: Partial<IQueueFilters>) => void;
  onClear: () => void;
  disabled?: boolean;
  /** Shown next to the search - "Showing 6 of 8,789 · 1,340 hidden as reviewed". */
  summary?: string | null;
}

interface IFilterFormFields {
  categoryId: string;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
}

// What the lock icon saves. The search box and the "work this group" filters are deliberately not
// part of it - a part number or a vehicle is a session's work, not a default.
type ISavedQueueFilters = Omit<IQueueFilters, "search" | "partNumber" | "vehicleMake" | "vehicleModel" | "vehicleGeneration">;

const FIRST_LISTED_OPTIONS: { value: FirstListedBucket | ""; label: string }[] = [
  { value: "", label: "Any listing age" },
  { value: "under30", label: `Listing ${FIRST_LISTED_BUCKET_LABELS.under30}` },
  { value: "d30to90", label: `Listing ${FIRST_LISTED_BUCKET_LABELS.d30to90}` },
  { value: "d90to180", label: `Listing ${FIRST_LISTED_BUCKET_LABELS.d90to180}` },
  { value: "over180", label: `Listing ${FIRST_LISTED_BUCKET_LABELS.over180}` },
];

const chipClasses = (active: boolean, disabled: boolean) =>
  classNames(
    "inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
    active ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    disabled ? "" : "cursor-pointer"
  );

const selectClasses =
  "h-9 rounded-md border border-gray-300 bg-white pl-3 pr-8 text-[13px] font-medium text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The queue's filter bar: search, category, location, listing-age bucket, the two part-number
 * and reviewed toggles, the extra signals, the sort and Clear. The lock icon saves the lot (bar
 * the search) under wilma:tableFilters:ebay-optimisation, same as the other tables.
 */
export default function QueueFilters({ filters, onChange, onClear, disabled = false, summary }: IQueueFiltersProps) {
  const { categories } = useCategoriesPartial();
  const { warehouseLocations } = useWarehouseLocations();

  // The category combobox and the location search keep their selection in internal state and
  // only speak react-hook-form, so they are bridged into the filter object here.
  const { register, setValue, watch } = useForm<IFilterFormFields>({
    defaultValues: {
      categoryId: filters.categoryId ?? "",
      locationSearchMode: filters.locationSearchMode,
      warehouseLocation: filters.warehouseLocation,
      warehouseLocationId: filters.warehouseLocation?.id,
      warehouseLocationBeginsWith: filters.warehouseLocationBeginsWith,
    },
  });
  // Bumped whenever the category or location is cleared from outside the inputs, so they remount.
  const [inputsKey, setInputsKey] = useState(0);

  const watchCategoryId = watch("categoryId");
  const watchLocationSearchMode = watch("locationSearchMode");
  const watchWarehouseLocation = watch("warehouseLocation");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const categoryList = useMemo(() => categories.data?.data ?? [], [categories.data]);

  useEffect(() => {
    const current = filtersRef.current;
    const categoryId = watchCategoryId || null;
    const next: Partial<IQueueFilters> = {};

    if (categoryId !== current.categoryId) {
      next.categoryId = categoryId;
      next.categoryName = categoryList.find((category) => category.id === categoryId)?.name ?? null;
    }
    if (watchLocationSearchMode !== current.locationSearchMode) next.locationSearchMode = watchLocationSearchMode;
    if ((watchWarehouseLocation?.id ?? null) !== (current.warehouseLocation?.id ?? null)) next.warehouseLocation = watchWarehouseLocation;
    if ((watchWarehouseLocationBeginsWith ?? "") !== current.warehouseLocationBeginsWith) {
      next.warehouseLocationBeginsWith = watchWarehouseLocationBeginsWith ?? "";
    }

    if (Object.keys(next).length > 0) onChangeRef.current(next);
  }, [watchCategoryId, watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, categoryList]);

  const clearCategory = useCallback(() => {
    setValue("categoryId", "");
    setInputsKey((key) => key + 1);
  }, [setValue]);

  const clearLocation = useCallback(() => {
    setValue("warehouseLocation", undefined);
    setValue("warehouseLocationId", undefined);
    setValue("warehouseLocationBeginsWith", "");
    setInputsKey((key) => key + 1);
  }, [setValue]);

  // The category can also be set from the Categories to work table, so follow it into the form.
  useEffect(() => {
    if ((filters.categoryId ?? "") !== (watchCategoryId ?? "")) {
      setValue("categoryId", filters.categoryId ?? "");
      setInputsKey((key) => key + 1);
    }
  }, [filters.categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setValue("categoryId", "");
    setValue("locationSearchMode", "equals");
    setValue("warehouseLocation", undefined);
    setValue("warehouseLocationId", undefined);
    setValue("warehouseLocationBeginsWith", "");
    setInputsKey((key) => key + 1);
    onClear();
  };

  const getDefaultFilters = useCallback((): ISavedQueueFilters => {
    const { search, partNumber, vehicleMake, vehicleModel, ...rest } = filtersRef.current;
    return rest;
  }, []);

  const applyDefaultFilters = useCallback(
    (saved: Partial<ISavedQueueFilters>) => {
      const next: Partial<IQueueFilters> = {
        categoryId: saved.categoryId ?? null,
        categoryName: saved.categoryName ?? null,
        locationSearchMode: saved.locationSearchMode ?? "equals",
        warehouseLocation: saved.warehouseLocation,
        warehouseLocationBeginsWith: saved.warehouseLocationBeginsWith ?? "",
        firstListedBucket: saved.firstListedBucket ?? null,
        missingPartNumbers: !!saved.missingPartNumbers,
        hideReviewedWithinDays:
          saved.hideReviewedWithinDays === undefined ? DEFAULT_HIDE_REVIEWED_DAYS : saved.hideReviewedWithinDays,
        pricedAboveIntelligence: !!saved.pricedAboveIntelligence,
        missingFields: Array.isArray(saved.missingFields) ? saved.missingFields : [],
        sort: QUEUE_SORT_OPTIONS.some((option) => option.key === saved.sort) ? (saved.sort as QueueSortKey) : "listingStarted",
      };

      setValue("categoryId", next.categoryId ?? "");
      setValue("locationSearchMode", next.locationSearchMode ?? "equals");
      setValue("warehouseLocation", next.warehouseLocation);
      setValue("warehouseLocationId", next.warehouseLocation?.id);
      setValue("warehouseLocationBeginsWith", next.warehouseLocationBeginsWith ?? "");
      setInputsKey((key) => key + 1);
      onChangeRef.current(next);
    },
    [setValue]
  );

  const toggleMissingField = (key: string) =>
    onChange({
      missingFields: filters.missingFields.includes(key)
        ? filters.missingFields.filter((field) => field !== key)
        : [...filters.missingFields, key],
    });

  const activeFilterPills = useMemo(() => {
    const pills: ITableFilterPill[] = [];

    if (filters.partNumber) {
      pills.push({ key: "partNumber", label: `Part number: ${filters.partNumber}`, onClear: () => onChange({ partNumber: null }) });
    }
    if (hasVehicleGroupFilter(filters)) {
      pills.push({
        key: "vehicle",
        label: `Vehicle: ${filters.vehicleMake} ${filters.vehicleModel}${
          filters.vehicleGeneration ? ` ${filters.vehicleGeneration}` : filters.vehicleGeneration === "" ? " no MK" : ""
        }`,
        onClear: () => onChange({ vehicleMake: null, vehicleModel: null, vehicleGeneration: null }),
      });
    }
    if (filters.categoryId) {
      pills.push({ key: "category", label: `Category: ${filters.categoryName ?? "1"}`, onClear: clearCategory });
    }
    if (filters.warehouseLocation?.id || filters.warehouseLocationBeginsWith) {
      pills.push({
        key: "location",
        label: `Location: ${filters.warehouseLocation?.displayName ?? filters.warehouseLocationBeginsWith}`,
        onClear: clearLocation,
      });
    }
    if (filters.firstListedBucket) {
      pills.push({
        key: "firstListed",
        label: `Listing ${FIRST_LISTED_BUCKET_LABELS[filters.firstListedBucket]}`,
        onClear: () => onChange({ firstListedBucket: null }),
      });
    }
    if (filters.missingPartNumbers) {
      pills.push({ key: "missingPartNumbers", label: "No part numbers", onClear: () => onChange({ missingPartNumbers: false }) });
    }
    if (filters.pricedAboveIntelligence) {
      pills.push({
        key: "pricedAboveIntelligence",
        label: "Priced above intelligence",
        onClear: () => onChange({ pricedAboveIntelligence: false }),
      });
    }
    filters.missingFields.forEach((field) => {
      pills.push({
        key: `missing-${field}`,
        label: `Missing: ${MISSING_FIELD_OPTIONS.find((option) => option.key === field)?.label ?? field}`,
        onClear: () => toggleMissingField(field),
      });
    });
    if (filters.hideReviewedWithinDays == null) {
      pills.push({
        key: "showReviewed",
        label: "Showing reviewed",
        onClear: () => onChange({ hideReviewedWithinDays: DEFAULT_HIDE_REVIEWED_DAYS }),
      });
    }

    return pills;
  }, [filters, clearCategory, clearLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const moreSignalsCount = (filters.pricedAboveIntelligence ? 1 : 0) + filters.missingFields.length;

  return (
    <form
      className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
      onSubmit={(e) => e.preventDefault()}
    >
      <TableFilterPanel
        activeFilterPills={activeFilterPills}
        disabled={disabled}
        defaultFiltersStorageKey={QUEUE_FILTERS_STORAGE_KEY}
        getDefaultFilters={getDefaultFilters}
        onApplyDefaultFilters={applyDefaultFilters}
      >
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <label className="relative flex min-w-[16rem] flex-1 items-center">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-4 w-4 text-gray-400" aria-hidden="true" />
            <span className="sr-only">Search part details</span>
            <input
              type="search"
              value={filters.search}
              onChange={(e) => onChange({ search: e.target.value })}
              disabled={disabled}
              placeholder="Search part details"
              className="h-9 w-full rounded-md border border-gray-300 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
            />
          </label>

          <div className="w-56">
            <ComboBoxInput
              key={`category-${inputsKey}`}
              label=""
              options={categoryList}
              searchField="name"
              valueField="id"
              register={register}
              setValue={setValue}
              registerName="categoryId"
              initialValue={filters.categoryId ?? ""}
              placeholder={categories.isLoading ? "Loading categories…" : "Category"}
              disabled={disabled || categories.isLoading}
              size="md"
            />
          </div>

          <div key={`location-${inputsKey}`} className="[&_label]:sr-only">
            <WarehouseLocationSearch
              showSearchOptions={true}
              register={register}
              setValue={setValue}
              watch={watch}
              isSearchDisabled={disabled}
              warehouseLocations={warehouseLocations}
            />
          </div>

          <label className="flex flex-col">
            <span className="sr-only">Listing age</span>
            <select
              value={filters.firstListedBucket ?? ""}
              onChange={(e) => onChange({ firstListedBucket: (e.target.value || null) as FirstListedBucket | null })}
              disabled={disabled}
              className={selectClasses}
            >
              {FIRST_LISTED_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => onChange({ missingPartNumbers: !filters.missingPartNumbers })}
            aria-pressed={filters.missingPartNumbers}
            disabled={disabled}
            className={chipClasses(filters.missingPartNumbers, disabled)}
            title="No MPN and no OE number. A generated WILM number or 'Not Available' counts as missing."
          >
            Missing part numbers
          </button>

          <button
            type="button"
            onClick={() =>
              onChange({ hideReviewedWithinDays: filters.hideReviewedWithinDays == null ? DEFAULT_HIDE_REVIEWED_DAYS : null })
            }
            aria-pressed={filters.hideReviewedWithinDays != null}
            disabled={disabled}
            className={chipClasses(filters.hideReviewedWithinDays != null, disabled)}
            title="Listings someone marked reviewed recently drop out of the queue"
          >
            Hide reviewed in last {DEFAULT_HIDE_REVIEWED_DAYS}d
          </button>

          <Popover className="relative">
            <PopoverButton disabled={disabled} className={chipClasses(moreSignalsCount > 0, disabled)}>
              More signals
              {moreSignalsCount > 0 && (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white/90 px-1 text-[11px] font-semibold text-primary-700">
                  {moreSignalsCount}
                </span>
              )}
              <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </PopoverButton>
            <PopoverPanel
              anchor={{ to: "bottom start", gap: 4 }}
              className="z-30 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Price</p>
              <label className="mt-1 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filters.pricedAboveIntelligence}
                  onChange={(e) => onChange({ pricedAboveIntelligence: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Priced above intelligence (+10%)
              </label>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Missing a field</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {MISSING_FIELD_OPTIONS.map((option) => {
                  const active = filters.missingFields.includes(option.key);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => toggleMissingField(option.key)}
                      aria-pressed={active}
                      className={classNames(
                        "rounded-full border px-2.5 py-1 text-xs font-medium transition",
                        active ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </PopoverPanel>
          </Popover>

          <label className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-gray-600">Sort</span>
            <select
              value={filters.sort}
              onChange={(e) => onChange({ sort: e.target.value as QueueSortKey })}
              disabled={disabled}
              className={selectClasses}
            >
              {QUEUE_SORT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="h-9 rounded-md px-3 text-[13px] font-medium text-primary-700 transition hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </TableFilterPanel>

      {summary && <p className="px-1 text-xs text-gray-500">{summary}</p>}
    </form>
  );
}

export { defaultQueueFilters };
